import { selectSelectedPricingPlan } from '../../../../../store/workspace/selectors';
import { applyReferralCodeThunk } from '../../../../../store/workspace/thunks';
import { CheckSolidIcon, LayerIcon } from '../../../../../assets/icons';
import { xhrDefaultMessage } from '../../../../../configs/notification';
import { thousandSeparator } from '../../../../../utils/numberHelpers';
import { useDispatch, useSelector } from '../../../../../store/store';
import { MAX_MEMBER_SEATS } from '../../constants';
import Button from '../../../../../components/Button';
import Input from '../../../../../components/Input';
import Modal from '../../../../../components/Modal';
import styles from './style.module.scss';
import { useState } from 'react';
import {
    setPurchasePlanModal,
    setContactSalesModal,
    setChooseCreditCardModal,
    setSelectedPlanSeatCount,
} from '../../../../../store/workspace/slice';

/** Summary of the plan just picked: extra seats, final price and referral code. */
export default function PurchasePlan() {
    const dispatch = useDispatch();
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [referralCode, setReferralCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showCounter, setShowCounter] = useState(false);
    const selectedPlan = useSelector(selectSelectedPricingPlan);
    const [isReferralApplied, setIsReferralApplied] = useState(false);

    const isAnnual = selectedPlan?.id.split('_')[1] === 'annual';
    const seatPrice = selectedPlan?.seatBasePrice || 0;
    const finalPrice = selectedPlan
        ? selectedPlan.finalPrice + (count * seatPrice * selectedPlan.monthCount)
        : 0;

    const closeModal = () => dispatch(setPurchasePlanModal(false));

    const confirmFunc = () => {
        dispatch(setSelectedPlanSeatCount(count));
        dispatch(setChooseCreditCardModal(true));
        closeModal();
    };

    const updateCount = (step: number) => {
        setCount(prev => Math.min(Math.max(prev + step, 0), MAX_MEMBER_SEATS));
    };

    const applyReferralCode = () => {
        setIsLoading(true);
        setErrorMessage('');

        /** The payment API validates the code on the read-only payment methods call. */
        dispatch(applyReferralCodeThunk({
            action: 'get_payment_methods',
            promotion: referralCode,
        }))
            .unwrap()
            .then(() => {
                setIsReferralApplied(true);
            })
            .catch(error => {
                setErrorMessage(error?.message || xhrDefaultMessage);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <Modal
            size='sm'
            actions='both'
            icon={<LayerIcon />}
            closeFunc={closeModal}
            childClassName={styles.wrapper}
            title={`${selectedPlan?.name} Plan`}
            dismissButton={{
                text: 'Cancel',
                type: 'Outline-Dark',
                disabled: isLoading,
                onClick: closeModal,
            }}
            confirmButton={{
                text: 'Continue',
                type: 'Primary',
                disabled: isLoading,
                onClick: confirmFunc,
            }}
        >
            <div className={styles.content}>
                <div>
                    <b>{selectedPlan?.totalJobCount}</b> Jobs and &nbsp;
                    <b>{selectedPlan?.totalApplicantCount}</b> applicant / &nbsp;
                    {isAnnual ? 'yearly' : 'monthly'}
                </div>

                <div className={styles.detail}>
                    There is one owner account included in this plan.
                    To add more, an additional charge applies. &nbsp;
                    {!showCounter && <b onClick={() => setShowCounter(true)}>Add member</b>}
                </div>

                {showCounter && <div className={styles.seat}>
                    <div className={styles.seat_count}>
                        <b>{count}</b> Member account seat{count > 1 ? 's' : ''} ${count * seatPrice} / mo.
                    </div>
                    <div className={styles.seat_action}>
                        <div
                            className={styles.seat_action_btn}
                            onClick={() => updateCount(-1)}
                            data-is-deactive={count === 0}
                        >
                            <span>-</span>
                        </div>
                        <span>{count}</span>
                        <div
                            className={styles.seat_action_btn}
                            onClick={() => updateCount(1)}
                            data-is-deactive={count === MAX_MEMBER_SEATS}
                        >
                            <span>+</span>
                        </div>
                    </div>
                </div>}

                {showCounter && count === MAX_MEMBER_SEATS && <div className={styles.contact_sales}>
                    <span>For more than {MAX_MEMBER_SEATS} seats please</span>
                    <b onClick={() => dispatch(setContactSalesModal(true))}>
                        contact sales
                    </b>
                </div>}

                <div className={styles.price}>
                    <span className={styles.price_discount}>
                        🎉 {isAnnual ? '%20 discount' : '7 Days Free Trial'}
                    </span>
                    <span className={styles.price_devider} />
                    <span className={styles.price_final}>
                        <b>${thousandSeparator(finalPrice)}</b> Final price
                    </span>
                </div>

                <div className={styles.referral}>
                    {isReferralApplied ? <div className={styles.referral_applied}>
                        <CheckSolidIcon />
                        <span>Referral code applied</span>
                    </div> : <>
                        <Input
                            type='text'
                            maxLength={15}
                            label='Referral code'
                            value={referralCode}
                            hasError={!!errorMessage}
                            wrapperClassName={styles.referral_input}
                            onChange={(value: string) => setReferralCode(value.toUpperCase())}
                        />
                        <Button
                            text='Apply'
                            type='Outline-Primary'
                            loading={isLoading}
                            onClick={applyReferralCode}
                            disabled={!referralCode.trim()}
                            className={styles.referral_btn}
                        />
                    </>}
                </div>

                {errorMessage && <span className={styles.error}>{errorMessage}</span>}
            </div>
        </Modal>
    );
};
